import cn from 'classnames'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Loader } from 'core-components/library/loader'

import { ICompletenessChartProps } from '../../../completenessChart'
import { IFinancialsChartProps } from '../../../financialsChart'
import { getInitialFilterForGPDetails } from '../../helpers/getInitialFilterForGPDetails'
import { getJbpGlobalPartnerDetails } from '../../helpers/getJbpGlobalPartnerDetails'
import { getValueTypeValue } from '../../helpers/getValueTypeValue'
import { IGlobalPartnerDetails } from '../../models/globalPartnerDetails'
import { getDataForCompletenessGlobalChart } from '../globalPartnerDetailsContent/helpers/getDataForCompletenessGlobalChart'
import { GlobalPartnerTableGraphs } from '../globalPartnerTableGraphs/GlobalPartnerTableGraphs'

import { IGlobalPartnerTableRowProps } from './models/globalPartnerTableRow'

import styles from './GlobalPartnerTableRow.module.scss'

export const GlobalPartnerTableRow: FC<IGlobalPartnerTableRowProps> = ({
  rowAttrs,
  item,
  renderChildren,
  rowState,
  additional,
  columnLength,
  onOpenPartnerDetails,
  valueType,
  onChangeValueType,
  fYear,
}) => {
  const { key, ...restRowAttrs } = rowAttrs
  /** -------------------- Variables ----------------------- */
  const [isOpened, setIsOpened] = useState(false)
  const [listedData, setListedData] = useState<IGlobalPartnerDetails[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const { currentFYear } = useSelector(selectJBPSettings)
  const crud = getJbpGlobalPartnerDetails(item.GlobalPartnerId)

  const completenessChart = useMemo<ICompletenessChartProps>(() => {
    const completenessChartData = getDataForCompletenessGlobalChart(listedData)
    const countOfFullyAcknowledgedPartners = completenessChartData.filter(
      (it) => it.IsFullyAcknowledged
    ).length

    const countOfPartnersWith100PercentCompleteness = completenessChartData.filter(
      (it) => !it.IsFullyAcknowledged && it.TotalCompleteness === 100 && !it.IsFullyAcknowledged
    ).length

    const countOfPartnerWithLess100PercentCount = completenessChartData.filter(
      (it) => it.TotalCompleteness < 100 && it.TotalCompleteness >= 50 && !it.IsFullyAcknowledged
    ).length

    const countOfPartnersWithLess50PercentCompleteness = completenessChartData.filter(
      (it) => it.TotalCompleteness < 50 && !it.IsFullyAcknowledged
    ).length

    return {
      countOfFullyAcknowledgedPartners,
      countOfPartnersWith100PercentCompleteness,
      countOfPartnerWithLess100PercentCount,
      countOfPartnersWithLess50PercentCompleteness,
    }
  }, [listedData])

  const financialChart = useMemo<IFinancialsChartProps>(() => {
    const rootLevel = listedData.filter((el) => el.ParentId === null)
    return {
      globalPartnerCurrentFYearTargets: getValueTypeValue({
        defaultValue: item.GlobalPartnerTargetsNdp,
        netValue: item.GlobalPartnerTargetsNet,
        tnetValue: item.GlobalPartnerTargetsTNet,
        utnetValue: item.GlobalPartnerTargetsUTNet,
        ndpValue: item.GlobalPartnerTargetsNdp,
        valueType,
      }),
      partnersCurrentFYearActuals: rootLevel.reduce((acc, it) => {
        const finCurrentYearActuals = getValueTypeValue({
          defaultValue: it.PartnerCurrentFYearActualsNdp,
          netValue: it.PartnerCurrentFYearActualsNet,
          tnetValue: it.PartnerCurrentFYearActualsTNet,
          utnetValue: it.PartnerCurrentFYearActualsUTNet,
          ndpValue: it.PartnerCurrentFYearActualsNdp,
          valueType,
        })
        return acc + finCurrentYearActuals
      }, 0),
      partnersPrevFYearActuals: rootLevel.reduce((acc, it) => {
        const finPrevFYearActuals = getValueTypeValue({
          defaultValue: it.PartnersPrevFYearActualsNdp,
          netValue: it.PartnersPrevFYearActualsNet,
          tnetValue: it.PartnersPrevFYearActualsTNet,
          utnetValue: it.PartnersPrevFYearActualsUTNet,
          ndpValue: it.PartnersPrevFYearActualsNdp,
          valueType,
        })
        return acc + finPrevFYearActuals
      }, 0),
      partnersCurrentFYearTargets: rootLevel.reduce((acc, it) => {
        const finCurrentFYearTargets = getValueTypeValue({
          defaultValue: it.PartnersCurrentFYearTargetsNdp,
          netValue: it.PartnersCurrentFYearTargetsNet,
          tnetValue: it.PartnersCurrentFYearTargetsTNet,
          utnetValue: it.PartnersCurrentFYearTargetsUTNet,
          ndpValue: it.PartnersCurrentFYearTargetsNdp,
          valueType,
        })
        return acc + finCurrentFYearTargets
      }, 0),

      valueType,
      rollupQuotas: 0, // count rollup quotas
      fYear: currentFYear,
    }
  }, [
    listedData,
    item.GlobalPartnerTargetsNet,
    item.GlobalPartnerTargetsTNet,
    item.GlobalPartnerTargetsUTNet,
    item.GlobalPartnerTargetsNdp,
    currentFYear,
    valueType,
  ])

  /** -------------------- Methods ----------------------- */
  const fetchGlobalPartnerDetails = useCallback(async () => {
    setIsLoading(true)

    const response = await crud.fetch()
    setListedData(response.items)

    setIsLoading(false)
  }, [crud])

  const toggleRowOpening = useCallback(() => {
    setIsOpened((prevState) => !prevState)

    fetchGlobalPartnerDetails()
  }, [fetchGlobalPartnerDetails])

  /** -------------------- Effects ----------------------- */
  useEffect(() => {
    crud.setFilter(getInitialFilterForGPDetails(item.GlobalPartnerId))

    return crud.subscribe('change', () => {
      if (isOpened) {
        fetchGlobalPartnerDetails()
      }
    })
  }, [crud, item.GlobalPartnerId, fetchGlobalPartnerDetails, isOpened])

  return (
    <>
      <tr
        key={key}
        {...restRowAttrs}
        className={cn({
          [styles.highlighted]: isOpened,
          [styles.highlightedHqPartners]: item.HQPartnerId,
          [styles.highlightedItsPartners]: item.IsITS,
        })}
      >
        <td className={styles.caret}>
          <div
            className={cn(styles.button_icon_caret, {
              [styles['icon_caret_down']]: isOpened,
              [styles['icon_caret_right']]: !isOpened,
            })}
            onClick={toggleRowOpening}
          >
            <i
              className={cn('kbase', {
                'kbase-caret-down': isOpened,
                'kbase-caret-right': !isOpened,
              })}
            />
          </div>
        </td>
        {renderChildren(item, rowState, additional)}
      </tr>
      {isOpened && (
        <tr key={`${key}-collapsed`}>
          <td className={styles.chartsCell} colSpan={columnLength}>
            {isLoading && <Loader />}
            {!isLoading && (
              <GlobalPartnerTableGraphs
                globalPartner={item}
                completenessChart={completenessChart}
                financialChart={financialChart}
                onOpenPartnerDetails={onOpenPartnerDetails}
                valueType={valueType}
                onChangeValueType={onChangeValueType}
                fYear={fYear}
              />
            )}
          </td>
        </tr>
      )}
    </>
  )
}
